import { aspects, palettes } from '../config';
import { elements } from '../../../dom';
import { drawTitleCard } from '../rendering/title-card';
import type { AspectId, AudioMeta, ImagePlacement, PaletteId, TemplateId, UploadedImage } from '../types';
import { formatBytes, formatDuration } from '../../../utils/format';

type PreviewState = {
  aspect: AspectId;
  audioMeta: AudioMeta | null;
  image: UploadedImage | null;
  imagePlacement: ImagePlacement;
  palette: PaletteId;
  subtitle: string;
  template: TemplateId;
  title: string;
};

type StatsState = {
  audioFile: File | null;
  duration: number;
  outputBlob: Blob | null;
  qualitySummary: string;
};

type PreviewStageOptions = {
  hasOutput: () => boolean;
  onPlay: () => void;
};

export function createPreviewStage({ hasOutput, onPlay }: PreviewStageOptions) {
  const context = elements.titleCanvas.getContext('2d');

  elements.playOverlay.addEventListener('click', onPlay);
  elements.resultVideo.addEventListener('play', () => {
    elements.playOverlay.hidden = true;
  });
  elements.resultVideo.addEventListener('pause', () => {
    elements.playOverlay.hidden = !hasOutput();
  });
  elements.resultVideo.addEventListener('ended', () => {
    elements.playOverlay.hidden = !hasOutput();
  });

  return {
    draw(state: PreviewState) {
      const aspect = aspects[state.aspect];
      const palette = palettes[state.palette];
      if (!context) {
        return;
      }

      if (elements.titleCanvas.width !== aspect.width || elements.titleCanvas.height !== aspect.height) {
        elements.titleCanvas.width = aspect.width;
        elements.titleCanvas.height = aspect.height;
        elements.titleCanvas.style.aspectRatio = `${aspect.width} / ${aspect.height}`;
        elements.previewFrame.style.aspectRatio = `${aspect.width} / ${aspect.height}`;
      }

      drawTitleCard(context, aspect.width, aspect.height, palette, {
        aspect: state.aspect,
        audioMeta: state.audioMeta,
        image: state.image,
        imagePlacement: state.imagePlacement,
        subtitle: state.subtitle,
        template: state.template,
        title: state.title,
      });
    },
    getCanvas() {
      return elements.titleCanvas;
    },
    async playVideo() {
      elements.playOverlay.hidden = true;
      try {
        await elements.resultVideo.play();
      } catch {
        elements.playOverlay.hidden = false;
      }
    },
    resetOutput() {
      elements.resultVideo.removeAttribute('src');
      elements.resultVideo.hidden = true;
      elements.playOverlay.hidden = true;
      elements.titleCanvas.hidden = false;
      elements.previewFrame.classList.remove('showing-video');
      elements.exportStats.hidden = true;
      elements.outputSize.textContent = '-';
    },
    showOutput(url: string) {
      elements.resultVideo.src = url;
      elements.resultVideo.hidden = false;
      elements.titleCanvas.hidden = true;
      elements.previewFrame.classList.add('showing-video');
      elements.playOverlay.hidden = false;
    },
    updateStats({ audioFile, duration, outputBlob, qualitySummary }: StatsState) {
      elements.qualityStat.textContent = qualitySummary;
      elements.inputSize.textContent = audioFile ? formatBytes(audioFile.size) : '-';
      elements.outputSize.textContent = outputBlob ? formatBytes(outputBlob.size) : '-';

      const hasDuration = Boolean(audioFile && duration > 0);
      elements.durationPill.hidden = !hasDuration;
      elements.durationPill.textContent = hasDuration ? formatDuration(duration) : '0s';
    },
    showStats() {
      elements.exportStats.hidden = false;
    },
  };
}
